/**
 * «Сайт» (`/site`): unirenter.ru по месяцам из копии Яндекс Метрики — визиты, визиты без отказа и
 * контакты (цель «Контакт») в среднем за день, чтобы неполный текущий месяц сравнивался честно.
 * Ниже — срез (`?slice=`): кто дал разницу текущего месяца с базой (среднее трёх прошлых), по
 * убыванию потерь. Роботов Метрика убирает сама. Спека `docs/specs/2026-09-30-metrika-design.md`;
 * числа — те же, что у `prokat-runner metrika-report`.
 */
import { useQuery } from "@tanstack/react-query";
import { useSearchParams } from "react-router";

import { loadMetrika, type Metric, type MetrikaReport as Data, type MetrikaRow } from "@/lib/metrika";

const card = "rounded-[var(--radius-card)] border border-border bg-bg-subtle";
const th = "whitespace-nowrap px-2.5 py-1.5 text-right font-medium";
const td = "whitespace-nowrap px-2.5 py-1.5 text-right tabular-nums";

const METRIC: Record<Metric, string> = { contacts: "Контакты", engaged: "Без отказа", visits: "Визиты" };
const MONTHS = [13, 25, 37, 100];

const fmt = (n: number) => (Math.abs(n) >= 100 ? Math.round(n).toLocaleString("ru-RU") : n.toFixed(1));
const pct = (d: number, base: number) => (base ? `${d > 0 ? "+" : ""}${Math.round((d * 100) / base)}%` : "—");
const tone = (d: number, base: number) =>
  !base || Math.abs(d / base) < 0.05 ? "text-fg-muted" : d < 0 ? "text-red-600" : "text-emerald-600";

function Tile({ label, v, base, d }: { label: string; v: number; base: number; d: number }) {
  return (
    <div className={`${card} flex min-w-0 flex-col gap-1 p-4`} data-annot={`плитка «${label}»`}>
      <span className="text-[12px] text-fg-muted">{label} в день</span>
      <span className="text-[22px] font-semibold tabular-nums tracking-[-0.02em]">{fmt(v)}</span>
      <span className={`text-[12px] tabular-nums ${tone(d, base)}`}>
        {pct(d, base)} к базе {fmt(base)}
      </span>
    </div>
  );
}

/** Верхняя таблица: итог сайта по месяцам, плюс доля контактов от визитов без отказа. */
function Totals({ d }: { d: Data }) {
  const t = d.total;
  const conv = t.engaged.map((e, i) => (e ? ((t.contacts[i] ?? 0) * 100) / e : 0));
  const row = (label: string, xs: number[], f: (n: number) => string = fmt) => (
    <tr className="border-t border-border" data-annot={`итог ${label}`}>
      <td className="whitespace-nowrap px-4 py-1.5">{label}</td>
      {xs.map((x, i) => (
        <td key={d.months[i]?.month ?? i} className={`${td} ${i === xs.length - 1 ? "font-medium" : ""}`}>
          {f(x)}
        </td>
      ))}
    </tr>
  );
  return (
    <section className={card} data-annot="сайт по месяцам">
      <div className="flex flex-wrap items-baseline gap-x-3 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">По месяцам, в среднем за день</h2>
        <span className="text-[12px] text-fg-faint">без роботов; текущий месяц — по {d.to}</span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium" />
              {d.months.map((m) => (
                <th key={m.month} className={th} title={`${m.days} дн.`}>
                  {m.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {row("Визиты", t.visits)}
            {row("Без отказа", t.engaged)}
            {row("Контакты", t.contacts)}
            {row("Контакты / без отказа", conv, (n) => `${n.toFixed(1)}%`)}
          </tbody>
        </table>
      </div>
    </section>
  );
}

function Breakdown({ d, metric, setMetric }: { d: Data; metric: Metric; setMetric: (m: Metric) => void }) {
  const rows = [...d.rows].sort((a, b) => a[`${metric}_delta`] - b[`${metric}_delta`]);
  const sum = (r: MetrikaRow) => r[`${metric}_delta`];
  const loss = rows.filter((r) => sum(r) < 0).reduce((s, r) => s + sum(r), 0);
  const current = d.months.at(-1)?.label ?? "";
  const slice = d.slices.find((s) => s.code === d.slice)?.label ?? d.slice;
  return (
    <section className={card} data-annot={`срез ${d.slice}`}>
      <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b border-border px-4 py-3">
        <h2 className="text-[13px] font-medium">{slice}: где разница</h2>
        <div className="flex gap-1">
          {(Object.keys(METRIC) as Metric[]).map((m) => (
            <button
              key={m}
              type="button"
              onClick={() => setMetric(m)}
              className={`rounded px-2 py-0.5 text-[12px] ${m === metric ? "bg-accent/15 font-medium" : "text-fg-muted hover:bg-bg-hover"}`}
            >
              {METRIC[m]}
            </button>
          ))}
        </div>
        <span className="text-[12px] text-fg-faint">
          {current} против среднего {d.base_months} мес. до него, в день; сверху — кто больше потерял
          {loss < 0 && ` (все потери ${fmt(loss)} в день)`}; строк {d.rows.length} из {d.rows_total}
        </span>
      </div>
      <div className="overflow-x-auto">
        <table className="w-full text-[12.5px]">
          <thead className="text-[11.5px] text-fg-faint">
            <tr>
              <th className="px-4 py-1.5 text-left font-medium">Значение</th>
              {d.months.map((m) => (
                <th key={m.month} className={th}>
                  {m.label}
                </th>
              ))}
              <th className={th}>База</th>
              <th className={th}>Δ в день</th>
              <th className={th}>Δ %</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const base = r[`${metric}_base`];
              const delta = r[`${metric}_delta`];
              return (
                <tr key={r.key} className="border-t border-border hover:bg-bg-hover" data-annot={`строка ${r.label}`}>
                  <td className="max-w-[22rem] truncate px-4 py-1.5" title={r.label}>
                    {r.label}
                  </td>
                  {r[metric].map((x, i) => (
                    <td
                      key={d.months[i]?.month ?? i}
                      className={`${td} ${x ? "" : "text-fg-faint"} ${i === r[metric].length - 1 ? "font-medium" : ""}`}
                    >
                      {x ? fmt(x) : "·"}
                    </td>
                  ))}
                  <td className={`${td} text-fg-muted`}>{fmt(base)}</td>
                  <td className={`${td} font-medium ${tone(delta, base)}`}>
                    {delta > 0 ? "+" : ""}
                    {fmt(delta)}
                  </td>
                  <td className={`${td} ${tone(delta, base)}`}>{pct(delta, base)}</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}

export function MetrikaPage() {
  const [sp, setSp] = useSearchParams();
  const slice = sp.get("slice") ?? "source";
  const months = Number(sp.get("months") ?? 13);
  const metric = (sp.get("metric") as Metric | null) ?? "contacts";
  const set = (k: string, v: string) =>
    setSp(
      (p) => {
        p.set(k, v);
        return p;
      },
      { replace: true },
    );
  const q = useQuery({ queryKey: ["metrika", slice, months], queryFn: () => loadMetrika(slice, months) });
  const d = q.data;
  const t = d?.total;
  return (
    <div className="flex flex-col gap-4">
      <header className="flex flex-wrap items-baseline gap-x-3 gap-y-2">
        <h1 className="text-[18px] font-semibold tracking-[-0.01em]">Сайт</h1>
        <span className="text-[12.5px] text-fg-muted">
          unirenter.ru, Яндекс Метрика {d ? `· счётчик ${d.counter}` : ""}
        </span>
        <div className="ml-auto flex gap-1">
          {MONTHS.map((n) => (
            <button
              key={n}
              type="button"
              onClick={() => set("months", String(n))}
              className={`rounded px-2 py-0.5 text-[12px] ${n === months ? "bg-accent/15 font-medium" : "text-fg-muted hover:bg-bg-hover"}`}
            >
              {n === 100 ? "всё" : `${n} мес.`}
            </button>
          ))}
        </div>
      </header>
      {q.isError && <p className="text-[13px] text-red-600">{String(q.error)}</p>}
      {q.isPending && <p className="text-[13px] text-fg-muted">Загружаю…</p>}
      {d && t && (
        <>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            {(["contacts", "engaged", "visits"] as Metric[]).map((m) => (
              <Tile key={m} label={METRIC[m]} v={t[m].at(-1) ?? 0} base={t[`${m}_base`]} d={t[`${m}_delta`]} />
            ))}
          </div>
          <Totals d={d} />
          <nav className="flex flex-wrap gap-1" data-annot="срезы">
            {d.slices
              .filter((s) => s.code !== "total")
              .map((s) => (
                <button
                  key={s.code}
                  type="button"
                  onClick={() => set("slice", s.code)}
                  className={`rounded-full border px-3 py-1 text-[12px] ${s.code === slice ? "border-accent bg-accent/15 font-medium" : "border-border text-fg-muted hover:bg-bg-hover"}`}
                >
                  {s.label}
                </button>
              ))}
          </nav>
          <Breakdown d={d} metric={metric} setMetric={(m) => set("metric", m)} />
        </>
      )}
    </div>
  );
}
